import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import type {
  HelpCenterAudience,
  IHelpCenterSection,
  IHelpCenterSubSection,
} from "../../interfaces/models/IHelpCenter";
import {
  findHelpCenterSection,
  getPublishedSubSections,
  sortByOrder,
} from "./helpCenterUtils";
import {
  audienceFromRouteParam,
  helpCenterCategoryEditPath,
  helpCenterCategoryPath,
} from "./helpCenterRoutes";
import { canViewHelpCenterCategory, getVisibleHelpCenterAudiences, isHelpCenterStaff, resolveWsgPermission } from "./helpCenterPermissions";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import HelpCenterPdfIcon from "./HelpCenterPdfIcon";
import HelpCenterPdfViewerModal from "./HelpCenterPdfViewerModal";
import HelpCenterTutorialVideoModal from "./HelpCenterTutorialVideoModal";
import { canPlayTutorialVideo } from "../../utils/videoEmbeds";
import { useHelpCenterConfig } from "./useHelpCenterConfig";
import "./HelpCenter.css";

function HelpCenterTutorialPreview({
  thumbnailUrl,
}: {
  thumbnailUrl?: string;
}) {
  return (
    <>
      <div className="help-center-category-tutorial-media">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="help-center-category-tutorial-thumbnail"
          />
        ) : (
          <div
            className="help-center-category-tutorial-placeholder"
            aria-hidden="true"
          />
        )}
        <div
          className="help-center-category-tutorial-overlay"
          aria-hidden="true"
        />
        <span className="help-center-category-tutorial-play" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="80"
            height="93"
            viewBox="0 0 80 93"
            fill="none"
          >
            <path
              d="M79.9101 46.44L0 0V92.88L79.9101 46.44Z"
              fill="white"
            />
          </svg>
        </span>
      </div>
      <span className="help-center-category-tutorial-label">
        Watch the Tutorial
      </span>
    </>
  );
}

function HelpCenterSubSectionSteps({
  subSection,
}: {
  subSection: IHelpCenterSubSection;
}) {
  const steps = sortByOrder(subSection.steps);

  return (
    <>
      <h2 className="help-center-subsection-title">{subSection.title}</h2>
      <div className="help-center-steps">
        {steps.map((step, index) => (
          <article key={step.id} className="help-center-step-card">
            <div className="help-center-step-content">
              <div className="help-center-step-number" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle cx="16" cy="16" r="16" fill="currentColor" />
                </svg>
                <span className="help-center-step-number-text">{index + 1}</span>
              </div>
              <p className="help-center-step-description">{step.description}</p>
            </div>
            {step.imageUrl ? (
              <div className="help-center-step-image-wrap">
                <div
                  className="help-center-step-image"
                  style={{
                    backgroundImage: `url("${step.imageUrl}")`,
                  }}
                  role="img"
                  aria-label=""
                />
              </div>
            ) : null}
          </article>
        ))}
        {steps.length === 0 ? (
          <p className="help-center-status">
            Content for this section is coming soon.
          </p>
        ) : null}
      </div>
    </>
  );
}

function resolveActiveSubSection(
  section: IHelpCenterSection,
  subSectionId?: string,
): IHelpCenterSubSection | undefined {
  const published = getPublishedSubSections(section);
  if (published.length === 0) {
    return undefined;
  }

  if (subSectionId) {
    const match = published.find((item) => item.id === subSectionId);
    if (match) {
      return match;
    }
  }

  return published[0];
}

export default function HelpCenterCategoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { audience: audienceParam, sectionId } = useParams();
  const audience = audienceFromRouteParam(audienceParam);
  const subsectionIdFromState = (
    location.state as { subsectionId?: string } | null
  )?.subsectionId;
  const { tokenDecoded, loggedPerson } = useAuth();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const { config, error, isReady } = useHelpCenterConfig(
    "Failed to load this Help Center topic.",
  );
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [activeSubSectionId, setActiveSubSectionId] = useState<
    string | undefined
  >(subsectionIdFromState);

  const permission = resolveWsgPermission(
    tokenDecoded?.extension_WSG_Permissions,
  );

  const visibleAudiences = useMemo(
    () =>
      getVisibleHelpCenterAudiences(permission, loggedPerson?.firmAdminAccess),
    [permission, loggedPerson?.firmAdminAccess],
  );

  const isStaff = isHelpCenterStaff(permission);

  useEffect(() => {
    if (subsectionIdFromState) {
      setActiveSubSectionId(subsectionIdFromState);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [subsectionIdFromState, location.pathname, navigate]);

  const section = useMemo(
    () =>
      config && audience && sectionId
        ? findHelpCenterSection(config, audience, sectionId)
        : null,
    [audience, config, sectionId],
  );

  const publishedSubSections = useMemo(
    () => (section ? getPublishedSubSections(section) : []),
    [section],
  );

  useEffect(() => {
    setIsTutorialModalOpen(false);
    setIsPdfModalOpen(false);
  }, [activeSubSectionId]);

  useEffect(() => {
    if (!isReady || !section) {
      return;
    }

    if (publishedSubSections.length === 0) {
      setActiveSubSectionId(undefined);
      return;
    }

    setActiveSubSectionId((current) => {
      if (current && publishedSubSections.some((item) => item.id === current)) {
        return current;
      }
      return publishedSubSections[0].id;
    });
  }, [isReady, publishedSubSections, section]);

  useEffect(() => {
    if (!section || !audience) {
      return;
    }

    const categoryPath = helpCenterCategoryPath(
      audience as HelpCenterAudience,
      section.id,
    ).toLowerCase();

    setPageMeta({
      title: section.title,
      breadcrumbLabels: {
        [categoryPath]: section.title,
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [audience, clearPageMeta, section, setPageMeta]);

  if (!audience || !sectionId) {
    return <Navigate to="/help-center" replace />;
  }

  if (!canViewHelpCenterCategory(audience, visibleAudiences)) {
    return <Navigate to="/help-center" replace />;
  }

  const activeSubSection = section
    ? resolveActiveSubSection(section, activeSubSectionId)
    : undefined;

  const hasPanelAside = Boolean(activeSubSection);
  const tutorialMediaUrl = activeSubSection?.tutorial?.mediaUrl;
  const pdfMediaUrl = activeSubSection?.pdfDownload?.mediaUrl;
  const hasTutorial = Boolean(tutorialMediaUrl?.trim());
  const hasPdf = Boolean(pdfMediaUrl?.trim());
  const canOpenTutorialModal = canPlayTutorialVideo(tutorialMediaUrl, pdfMediaUrl);

  return (
    <div className="w-full pb-20">
      <div className="w-full bg-white help-center-shell">
        <div className="help-center-content">
          {error && isReady && !section ? (
            <p className="help-center-status help-center-status-error">{error}</p>
          ) : null}

          {section ? (
            <div className="help-center-category-layout help-center-fade-in">
              <header className="help-center-category-header">
                <div className="help-center-category-title-row">
                  <h1 className="help-center-category-title">{section.title}</h1>
                  {isStaff ? (
                    <button
                      type="button"
                      className="help-center-edit-category-button"
                      onClick={() =>
                        navigate(
                          helpCenterCategoryEditPath(
                            audience as HelpCenterAudience,
                            section.id,
                          ),
                        )
                      }
                    >
                      <span className="help-center-edit-category-button-content">
                        <span>Edit Category</span>
                        <span
                          className="help-center-edit-category-button-icon"
                          aria-hidden="true"
                        >
                          <Pencil width={16} height={16} color="currentColor" />
                        </span>
                      </span>
                    </button>
                  ) : null}
                </div>
                {section.description ? (
                  <p className="help-center-category-description">
                    {section.description}
                  </p>
                ) : null}
              </header>

              <div className="help-center-category-body">
                {activeSubSection ? (
                  <div className="help-center-category-panel">
                    <div className="help-center-category-main">
                      <HelpCenterSubSectionSteps subSection={activeSubSection} />
                    </div>

                    {hasPanelAside ? (
                      <>
                        <div
                          className="help-center-category-panel-separator help-center-category-panel-separator-vertical"
                          role="presentation"
                        />
                        <div className="help-center-category-on-this-section-frame">
                          {publishedSubSections.length > 0 ? (
                            <div className="help-center-category-on-this-section-top">
                              <div className="help-center-category-on-this-section-header">
                                In this section
                              </div>
                              <nav className="help-center-category-sidebar-nav help-center-category-on-this-section-links">
                                {publishedSubSections.map((subSection) => {
                                  const isActive =
                                    subSection.id === activeSubSection?.id;
                                  return (
                                    <button
                                      key={subSection.id}
                                      type="button"
                                      onClick={() =>
                                        setActiveSubSectionId(subSection.id)
                                      }
                                      className={`help-center-category-sidebar-link${
                                        isActive
                                          ? " help-center-category-sidebar-link-active"
                                          : ""
                                      }`}
                                    >
                                      {subSection.title}
                                    </button>
                                  );
                                })}
                              </nav>
                            </div>
                          ) : null}

                          {hasTutorial ? (
                            <div className="help-center-category-on-this-section-video">
                              {canOpenTutorialModal ? (
                                <button
                                  type="button"
                                  className="help-center-category-tutorial-link"
                                  onClick={() => setIsTutorialModalOpen(true)}
                                >
                                  <HelpCenterTutorialPreview
                                    thumbnailUrl={
                                      activeSubSection.tutorial?.thumbnailUrl
                                    }
                                  />
                                </button>
                              ) : (
                                <div className="help-center-category-tutorial-link">
                                  <HelpCenterTutorialPreview
                                    thumbnailUrl={
                                      activeSubSection.tutorial?.thumbnailUrl
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          ) : null}

                          {hasPdf ? (
                            <div className="help-center-category-on-this-section-pdf">
                              <button
                                type="button"
                                className="help-center-category-pdf-link"
                                onClick={() => setIsPdfModalOpen(true)}
                              >
                                <div className="help-center-category-pdf-media">
                                  {activeSubSection.pdfDownload?.thumbnailUrl ? (
                                    <img
                                      src={
                                        activeSubSection.pdfDownload.thumbnailUrl
                                      }
                                      alt=""
                                      className="help-center-category-tutorial-thumbnail"
                                    />
                                  ) : (
                                    <div
                                      className="help-center-category-tutorial-placeholder"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <div
                                    className="help-center-category-tutorial-overlay"
                                    aria-hidden="true"
                                  />
                                  <span
                                    className="help-center-category-pdf-icon"
                                    aria-hidden="true"
                                  >
                                    <HelpCenterPdfIcon />
                                  </span>
                                </div>
                                <span className="help-center-category-tutorial-label">
                                  View the PDF Instructions
                                </span>
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <p className="help-center-status">
                    Content for this category is coming soon.
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {isReady && !error && !section ? (
            <p className="help-center-status help-center-status-error">
              This Help Center topic could not be found.
            </p>
          ) : null}
        </div>
      </div>

      <HelpCenterTutorialVideoModal
        isOpen={isTutorialModalOpen}
        onClose={() => setIsTutorialModalOpen(false)}
        videoUrl={tutorialMediaUrl}
      />

      <HelpCenterPdfViewerModal
        isOpen={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        pdfUrl={pdfMediaUrl}
        title="PDF Instructions"
      />
    </div>
  );
}
